import { useState, useEffect, useCallback } from 'react';
import { supabase, CmsHeroSlide } from '@/lib/supabase';
import ImageUploader from '@/components/base/ImageUploader';
import { writeLog } from '@/hooks/useAdminRole';

interface EditForm {
  title: string;
  subtitle: string;
  description: string;
  image_url: string;
  align: 'left' | 'center' | 'right';
  small_title: boolean;
}

const ALIGN_OPTIONS: { value: 'left' | 'center' | 'right'; label: string; icon: string }[] = [
  { value: 'left', label: '左对齐', icon: 'ri-align-left' },
  { value: 'center', label: '居中', icon: 'ri-align-center' },
  { value: 'right', label: '右对齐', icon: 'ri-align-right' },
];

export default function HeroCMS() {
  const [list, setList] = useState<CmsHeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editData, setEditData] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cms_hero_slides')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setList(data || []);
    } catch {
      showToast('加载失败，请刷新重试', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleEdit = (item: CmsHeroSlide) => {
    setEditId(item.id);
    setEditData({
      title: item.title,
      subtitle: item.subtitle,
      description: item.description,
      image_url: item.image_url,
      align: item.align,
      small_title: item.small_title,
    });
  };

  const handleSave = async () => {
    if (!editId || !editData) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('cms_hero_slides')
        .update({
          title: editData.title,
          subtitle: editData.subtitle,
          description: editData.description,
          image_url: editData.image_url,
          align: editData.align,
          small_title: editData.small_title,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editId);
      if (error) throw error;
      showToast('保存成功');
      await writeLog({ module: '首页轮播', action: '编辑', target_name: editData.title });
      setEditId(null);
      setEditData(null);
      await fetchList();
    } catch {
      showToast('保存失败，请重试', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (item: CmsHeroSlide) => {
    try {
      const { error } = await supabase
        .from('cms_hero_slides')
        .update({ is_published: !item.is_published, updated_at: new Date().toISOString() })
        .eq('id', item.id);
      if (error) throw error;
      showToast(item.is_published ? '已隐藏' : '已显示');
      await fetchList();
    } catch {
      showToast('操作失败', 'error');
    }
  };

  const handleCreate = async () => {
    try {
      const maxOrder = list.length > 0 ? Math.max(...list.map((s) => s.sort_order)) : 0;
      const { error } = await supabase.from('cms_hero_slides').insert({
        title: '新轮播标题',
        subtitle: '副标题',
        description: '描述文字',
        image_url: '',
        align: 'center',
        small_title: false,
        is_published: false,
        sort_order: maxOrder + 1,
      });
      if (error) throw error;
      showToast('已创建新轮播，请编辑内容');
      await fetchList();
    } catch {
      showToast('创建失败', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这张轮播吗？')) return;
    try {
      const { error } = await supabase.from('cms_hero_slides').delete().eq('id', id);
      if (error) throw error;
      showToast('已删除');
      await fetchList();
    } catch {
      showToast('删除失败', 'error');
    }
  };

  const handleMoveOrder = async (item: CmsHeroSlide, direction: 'up' | 'down') => {
    const idx = list.findIndex((s) => s.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const swapItem = list[swapIdx];
    try {
      await Promise.all([
        supabase.from('cms_hero_slides').update({ sort_order: swapItem.sort_order }).eq('id', item.id),
        supabase.from('cms_hero_slides').update({ sort_order: item.sort_order }).eq('id', swapItem.id),
      ]);
      await fetchList();
    } catch {
      showToast('排序失败', 'error');
    }
  };

  const publishedCount = list.filter((s) => s.is_published).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">首页轮播管理</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            共 {list.length} 张轮播 · {publishedCount} 张显示中
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>新建轮播
        </button>
      </div>

      {/* 提示 */}
      <div className="flex items-start gap-2 bg-teal-50 border border-teal-100 rounded-lg px-4 py-3">
        <i className="ri-information-line text-teal-500 mt-0.5 flex-shrink-0"></i>
        <p className="text-xs text-teal-700 leading-relaxed">
          修改后刷新官网首页即可看到最新效果。建议保持 3-5 张轮播，图片建议使用 1920×1080 横向图片效果最佳。
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
              <div className="flex gap-4">
                <div className="w-32 h-20 bg-gray-100 rounded-lg flex-shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-100 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {list.map((item, idx) => (
            <div
              key={item.id}
              className={`bg-white rounded-xl border transition-all ${
                item.is_published ? 'border-gray-100' : 'border-gray-200 opacity-60'
              }`}
            >
              {editId === item.id ? (
                /* ---- 编辑态 ---- */
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">主标题</label>
                      <input
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                        value={editData?.title || ''}
                        onChange={(e) => setEditData((d) => d ? { ...d, title: e.target.value } : d)}
                        placeholder="如：技术驱动创新"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">副标题 <span className="text-teal-500">（teal色高亮）</span></label>
                      <input
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                        value={editData?.subtitle || ''}
                        onChange={(e) => setEditData((d) => d ? { ...d, subtitle: e.target.value } : d)}
                        placeholder="如：智能赋能未来"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">描述文字</label>
                    <input
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                      value={editData?.description || ''}
                      onChange={(e) => setEditData((d) => d ? { ...d, description: e.target.value } : d)}
                      placeholder="如：福建龙创未来股份有限公司"
                    />
                  </div>

                  <ImageUploader
                    label="背景图片（建议 1920×1080）"
                    value={editData?.image_url || ''}
                    onChange={(url) => setEditData((d) => d ? { ...d, image_url: url } : d)}
                    placeholder="粘贴图片 URL，如 https://..."
                    previewWidth="w-48"
                    previewHeight="h-28"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 mb-2 block">文字对齐方式</label>
                      <div className="flex gap-2">
                        {ALIGN_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setEditData((d) => d ? { ...d, align: opt.value } : d)}
                            className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border text-xs transition-all cursor-pointer ${
                              editData?.align === opt.value
                                ? 'border-teal-500 bg-teal-50 text-teal-700'
                                : 'border-gray-200 text-gray-500 hover:border-gray-300'
                            }`}
                          >
                            <i className={`${opt.icon} text-base`}></i>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-2 block">字体大小</label>
                      <div className="flex gap-2">
                        {[
                          { value: false, label: '大字', desc: '默认' },
                          { value: true, label: '小字', desc: '标题较长时用' },
                        ].map((opt) => (
                          <button
                            key={String(opt.value)}
                            type="button"
                            onClick={() => setEditData((d) => d ? { ...d, small_title: opt.value } : d)}
                            className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-lg border text-xs transition-all cursor-pointer ${
                              editData?.small_title === opt.value
                                ? 'border-teal-500 bg-teal-50 text-teal-700'
                                : 'border-gray-200 text-gray-500 hover:border-gray-300'
                            }`}
                          >
                            <span className="font-medium">{opt.label}</span>
                            <span className="text-gray-400">{opt.desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={() => { setEditId(null); setEditData(null); }}
                      className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                /* ---- 预览态 ---- */
                <div className="flex items-start gap-4 p-5">
                  {/* 缩略图 */}
                  <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 relative">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <i className="ri-image-line text-2xl"></i>
                      </div>
                    )}
                    <div className="absolute bottom-1 right-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                      #{item.sort_order}
                    </div>
                  </div>

                  {/* 信息 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-800">{item.title}</h3>
                      <span className="text-sm text-teal-600 font-medium">/ {item.subtitle}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_published ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.is_published ? '显示中' : '已隐藏'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mb-1.5">{item.description}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className={`${ALIGN_OPTIONS.find((o) => o.value === item.align)?.icon} text-sm`}></i>
                        {ALIGN_OPTIONS.find((o) => o.value === item.align)?.label}
                      </span>
                      {item.small_title && (
                        <span className="text-xs text-gray-400">小字模式</span>
                      )}
                    </div>
                  </div>

                  {/* 操作 */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* 排序 */}
                    <button
                      onClick={() => handleMoveOrder(item, 'up')}
                      disabled={idx === 0}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="上移"
                    >
                      <i className="ri-arrow-up-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleMoveOrder(item, 'down')}
                      disabled={idx === list.length - 1}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="下移"
                    >
                      <i className="ri-arrow-down-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                      title="编辑"
                    >
                      <i className="ri-edit-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleTogglePublish(item)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                      title={item.is_published ? '隐藏' : '显示'}
                    >
                      <i className={`${item.is_published ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                      title="删除"
                    >
                      <i className="ri-delete-bin-line text-base"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 text-white text-sm px-4 py-2.5 rounded-lg z-50 ${toastType === 'error' ? 'bg-rose-600' : 'bg-gray-800'}`}>
          <div className="flex items-center gap-2">
            <i className={`${toastType === 'error' ? 'ri-error-warning-line text-rose-200' : 'ri-checkbox-circle-line text-emerald-400'}`}></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
